import { chunkAudioFile } from "@/lib/audio/chunker";
import { isNoConfiguredProviderResponse, NoConfiguredProviderError } from "@/lib/edgeFnError";
import type { UploadConsultaResult } from "../types";

export type UploadStage =
  | "idle"
  | "hashing"
  | "chunking"
  | "uploading"
  | "transcribing"
  | "analyzing"
  | "done"
  | "error";

export interface UploadConsultaOptions {
  file: File;
  label: string;
  token: string;
  onStageChange?: (stage: UploadStage) => void;
  signal?: AbortSignal;
}

interface UploadChunkResult {
  firstResult: UploadConsultaResult | null;
  orphanedDuplicate: boolean;
}

function createRequestId(): string {
  const cryptoApi = globalThis.crypto;
  if (cryptoApi && typeof cryptoApi.randomUUID === "function") {
    return cryptoApi.randomUUID();
  }

  if (cryptoApi && typeof cryptoApi.getRandomValues === "function") {
    const bytes = new Uint8Array(16);
    cryptoApi.getRandomValues(bytes);
    // RFC4122 v4 bits
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0"));
    return `${hex.slice(0, 4).join("")}-${hex.slice(4, 6).join("")}-${hex.slice(6, 8).join("")}-${hex.slice(8, 10).join("")}-${hex.slice(10, 16).join("")}`;
  }

  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function getFunctionsBaseUrl(): string {
  const supabaseUrl =
    (import.meta.env.VITE_SUPABASE_URL as string | undefined) ?? "";
  if (!supabaseUrl) throw new Error("VITE_SUPABASE_URL is not configured");
  return supabaseUrl.replace(/\/$/, "") + "/functions/v1/llm-proxy";
}

export async function uploadConsulta({
  file,
  label,
  token,
  onStageChange,
  signal,
}: UploadConsultaOptions): Promise<UploadConsultaResult> {
  const notify = (s: UploadStage) => onStageChange?.(s);

  const MAX_BYTES = 100 * 1024 * 1024;
  if (file.size > MAX_BYTES) {
    throw new Error("Arquivo muito grande. Máximo 100MB para demo.");
  }

  // Reject silent / empty / sub-second recordings before they reach Whisper.
  // 10KB ≈ 1s of compressed audio. Whisper hallucinates "you" / "Thank you"
  // on tiny inputs, so we fail fast with a clear message.
  const MIN_AUDIO_BYTES = 10_000;
  if (file.size < MIN_AUDIO_BYTES) {
    throw new Error(
      "Arquivo de áudio muito pequeno ou vazio. Grave por pelo menos 3 segundos.",
    );
  }

  notify("hashing");
  // Each upload must generate a brand-new analysis, even for the same file.
  const uploadRequestId = createRequestId();

  notify("chunking");
  const chunks = await chunkAudioFile(file, 24);

  const baseUrl = getFunctionsBaseUrl();

  const sendChunks = async (keyBase: string): Promise<UploadChunkResult> => {
    notify("uploading");
    let firstResult: UploadConsultaResult | null = null;
    let orphanedDuplicate = false;

    for (let i = 0; i < chunks.length; i++) {
      const chunk = chunks[i];
      const form = new FormData();
      form.append("file", chunk, chunk.name);
      form.append("label", chunks.length === 1 ? label : `${label} (parte ${i + 1}/${chunks.length})`);
      form.append(
        "idempotency_key",
        chunks.length === 1 ? keyBase : `${keyBase}_chunk${i}`,
      );

      notify(i === 0 ? "transcribing" : "uploading");

      const resp = await fetch(`${baseUrl}/transcribe`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: form,
        signal,
      });

      if (!resp.ok) {
        const errBody = await resp.json().catch(() => ({ error: resp.statusText }));
        if (errBody.error === "duplicate") {
          // A duplicate here means key collision or stale reuse attempt.
          // We force one retry with a fresh key to preserve "new upload = new analysis".
          orphanedDuplicate = true;
          firstResult = null;
          break;
        }
        if (errBody.error === "file_too_large_for_whisper") {
          throw new Error(`Arquivo muito grande para transcrição (máximo ${String(errBody.max_mb ?? 25)} MB por trecho).`);
        }
        if (
          errBody.error === "no_transcription_key" ||
          isNoConfiguredProviderResponse(resp.status, errBody)
        ) {
          throw new NoConfiguredProviderError(
            typeof errBody.message === "string" && errBody.message.length > 0
              ? errBody.message
              : typeof errBody.detail === "string"
                ? errBody.detail
                : "no_configured_provider",
            { status: resp.status, code: errBody.error },
          );
        }
        throw new Error(
          errBody.error
            ? `Erro do servidor: ${String(errBody.error)}`
            : `Erro HTTP ${resp.status}`,
        );
      }

      const data = (await resp.json()) as { job_id: string; consulta_id: string };
      if (!firstResult) {
        firstResult = { jobId: data.job_id, consultaId: data.consulta_id };
      }
    }

    return { firstResult, orphanedDuplicate };
  };

  let { firstResult, orphanedDuplicate } = await sendChunks(uploadRequestId);

  if (orphanedDuplicate) {
    const retryKey = createRequestId();
    ({ firstResult, orphanedDuplicate } = await sendChunks(retryKey));
  }

  notify("analyzing");

  if (!firstResult) {
    throw new Error("Nenhum resultado retornado do servidor.");
  }

  if (!firstResult.consultaId || orphanedDuplicate) {
    throw new Error(
      "Nao foi possivel iniciar uma nova analise para este upload. Tente novamente.",
    );
  }

  return firstResult;
}
